import { getApp } from 'firebase/app';
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
  type Functions,
} from 'firebase/functions';

let functions: Functions;

// Call after initAuth() / initFirestore() — reuses the default Firebase app.
export function initFunctions(): Functions {
  functions = getFunctions(getApp());

  try {
    if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
      connectFunctionsEmulator(functions, 'localhost', 5001);
    }
  } catch {
    // Not in a Vite environment (tests / SSR) — no emulator wiring.
  }

  return functions;
}

export function getFunctionsInstance(): Functions {
  if (!functions) {
    throw new Error('Functions not initialized. Call initFunctions() first.');
  }
  return functions;
}

// Thin typed wrapper around a callable Cloud Function.
export function callable<Req = unknown, Res = unknown>(name: string): (data: Req) => Promise<Res> {
  return async (data: Req): Promise<Res> => {
    const fn = httpsCallable<Req, Res>(getFunctionsInstance(), name);
    const res = await fn(data);
    return res.data;
  };
}

const REGION = 'us-central1';

// For a plain onRequest endpoint (not onCall) that a page reaches via a
// raw fetch() rather than the callable() wrapper above — e.g. the public
// contract e-sign endpoints, invoked from a page with no Firebase Auth
// session at all. Mirrors functions/src/urls.ts's functionUrl() logic but
// reads Vite's env instead of process.env, since this runs in the browser.
export function functionsBaseUrl(): string {
  try {
    const env = (import.meta as { env?: { DEV?: boolean; VITE_FIREBASE_PROJECT_ID?: string } }).env;
    const projectId = env?.VITE_FIREBASE_PROJECT_ID ?? 'demo-gigdriver';
    if (env?.DEV) {
      return `http://localhost:5001/${projectId}/${REGION}`;
    }
    return `https://${REGION}-${projectId}.cloudfunctions.net`;
  } catch {
    // Not in a Vite environment (tests / SSR).
    return `https://${REGION}-demo-gigdriver.cloudfunctions.net`;
  }
}
